<template>
  <div>
    <vxe-grid
      ref="vxeGrid"
      auto-resize
      highlight-hover-row
      :data="tableData"
      :toolbar-config="tableToolbar"
      :columns="tableColumns"
      :loading="loading"
      :height="700"
      size="small"
      resizable
      :seq-config="{
        startIndex: (page.curPage - 1) * page.pageSize,
      }"
      row-id="amzSkuCode"
      :checkbox-config="{ highlight: true, reserve: true }"
      @cell-click="onCellClick"
      @checkbox-change="onCheckboxChange"
      @checkbox-all="onCheckboxChange"
    >
      <template v-slot:pager>
        <pagination
          size="small"
          align="right"
          :current-page="page.curPage"
          :page-size="page.pageSize"
          :page-total="page.pageTotal"
          @change="handlePaginationChange"
        />
      </template>
      <template v-slot:defaultImageUrl="{ row }">
        <el-image
          :src="
            row.defaultImageUrl
              ? $osscdn(row.defaultImageUrl)
              : '/image/static/img/nopic-square.jpg'
          "
          :preview-src-list="[
            row.defaultImageUrl
              ? $osscdn(row.defaultImageUrl)
              : '/image/static/img/nopic-square.jpg',
          ]"
          style="width:60px; height:60px"
        >
          <div slot="error" class="image-slot">
            <img
              src="/image/static/img/nopic-square.jpg"
              style="width:60px; height:60px"
            />
          </div>
        </el-image>
      </template>
      <template v-slot:opt="{ row }">
        <div style="text-align: center">
          <el-dropdown trigger="click" @command="handleCommand">
            <span class="el-dropdown-link">
              {{ $l('INDEX.choose_fabric_function') || '功能' }}
              <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-for="(opt, index) in tableOpts"
                :key="index"
                :icon="opt.icon"
                :command="{ opt, data: row }"
                :divided="opt.divided"
                :disabled="opt.disabled ? opt.disabled(row) : false"
              >
                {{ opt.text }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </template>
      <template v-slot:status="{ row }">
        <el-tag
          size="mini"
          :type="
            row.amzSkuStatus === 'D'
              ? 'danger'
              : row.amzSkuStatus === 'Q'
              ? 'primary'
              : row.amzSkuStatus === 'P'
              ? 'warning'
              : row.amzSkuStatus === 'C'
              ? 'confirm'
              : 'success'
          "
        >
          {{ row.v_amzSkuStatus }}
        </el-tag>
        <el-tag
          v-if="
            row.v_applicationStatus &&
              !['M', 'C'].includes(row.applicationStatus)
          "
          size="mini"
          class="ml-2"
          effect="plain"
          :type="
            ['R', 'T', 'F', 'U'].includes(row.applicationStatus)
              ? 'danger'
              : ['P', 'O', 'A', 'S'].includes(row.applicationStatus)
              ? 'light'
              : 'success'
          "
        >
          {{ row.v_applicationStatus }}
        </el-tag>
        <div v-if="row.rejectReason" style="color:red" class="mt-2">
          {{ row.rejectReason }}
        </div>
      </template>
      <template v-slot:displayName="{ row }">
        <span>{{ row.goodsName }}</span>
        <br />
        <span>{{ row.goodsNameEn }}</span>
        <br />
        <span>SKU:</span><span style="color: #409EFF">{{ row.amzSkuNo }}</span>
      </template>
      <template v-slot:displayBrand="{ row }">
        <span style="color: #303133">{{ row.brandName }}</span>
        <br />
        <span style="color: #303133">{{ row.categoryFullName }}</span>
        <br />
        <span style="color: #303133">{{ row.collectionName }}</span>
      </template>
      <template v-slot:displayColor="{ row }">
        <span style="color: #303133">{{ row.goodsSize }}</span>
        <br />
        <span style="color: #303133"
          >{{ row.goodsColor }} &nbsp; {{ row.goodsColorEn }}</span
        >
        <br />
        <span style="color: #303133">{{ row.goodsPattern }}</span>
      </template>
      <template v-slot:displayUnit="{ row }">
        <span>UNIT:</span
        ><span style="color: #303133"
          >{{ row.unitLength }}x{{ row.unitWidth }}x{{
            row.unitHeight
          }}
          cm</span
        >
        <br />
        <span>N.W:</span
        ><span style="color: #303133">{{ row.goodsWeight }} kg</span>
        <br />
        <span>G.W:</span
        ><span style="color: #303133">{{ row.unitWeight }} kg</span>
        <br />
        <span>CBM:</span
        ><span style="color: #303133">{{ row.unitCbm }} m³</span>
      </template>
      <template v-slot:displayBox="{ row }">
        <span
          >CTN:{{ row.ctnLength }}x{{ row.ctnWidth }}x{{
            row.ctnHeight
          }}
          cm</span
        >
        <br />
        <span>N.W:{{ row.ctnNetWeight }} kg</span>
        <br />
        <span>G.W:{{ row.ctnGrossWeight }} kg</span>
        <br />
        <span>CBM:{{ row.ctnCbm }} m³</span>
      </template>
      <template v-slot:designAndBuyer="{ row }">
        <span>PM：</span
        ><span style="color: #303133">{{ row.designName }}</span>
        <br />
        <span>PC：</span><span style="color: #303133">{{ row.buyerName }}</span>
      </template>
      <template v-slot:displayFactory="{ row }">
        <span>NC:</span><span style="color: #303133">{{ row.ncNo }}</span>
        <br />
        <span style="color: #303133">{{ row.scmName }}</span>
      </template>
      <template v-slot:isVc="{ row }">
        <el-tag
          size="mini"
          v-if="row.isVc"
          :type="row.isVc === 'N' ? 'info' : 'warning'"
        >
          {{ row.v_isVc }}
        </el-tag>
      </template>
      <template v-slot:createInfo="{ row }">
        <span>{{ row.createByName }}</span>
        <br />
        <span>{{ row.createDate }}</span>
      </template>
      <template v-slot:modifyInfo="{ row }">
        <span>{{ row.modifyByName }}</span>
        <br />
        <span>{{ row.modifyDate }}</span>
      </template>
      <template v-slot:empty="{}">
        <div style="text-align: center">
          <img src="/image/static/img/nodata.png" />
          <p class="no-data">
            {{ $l('INDEX.tips_no_data') || '暂无数据！' }}
          </p>
        </div>
      </template>
      <template v-slot:toolbar_form>
        <slot name="header">
          <Header
            ref="header"
            :addSku="addSku"
            @query="getData"
            @reset="resetPage"
            @resetChecked="resetChecked"
            @informationReview="informationReview"
            @purchasableApply="purchasableApply"
            :checkedCount="checkedCount"
            @updateDesignVisible="
              () => {
                updateDesignVisible = true
              }
            "
            @chooseAmzSkuVisible="
              () => {
                chooseAmzSkuVisible = true
              }
            "
            @handleCommand="
              handleCommand({
                opt: { pageType: 'add' },
                data: { amzSkuCode: '' },
              })
            "
            @remove="remove()"
            @uploadTech="uploadTech()"
          >
          </Header>
        </slot>
      </template>
    </vxe-grid>

    <ChooseAmzSku
      :visible.sync="chooseAmzSkuVisible"
      v-show="amzSpuCode != ''"
      url="/ecom/amz/sku/listByDemandWithoutSpuCode"
      :amzSkuStatus="['Y', 'Q']"
      multiple
      confirm-on-close
      reserve
      @confirm="chooseAmzSkuConfirm"
    />

    <AddSku
      :visible.sync="amzSkuOption.addAmzSkuVisible"
      confirm-on-close
      reserve
      :amzSpuCode="amzSpuCode"
      :amzSkuCode="amzSkuOption.amzSkuCode"
      :disabled="amzSkuOption.amzSkudisabled"
      :isSpuCopy="amzSkuOption.isSpuCopy"
      :title="amzSkuOption.title"
      :form="form"
      @close="headerQuery"
    />

    <ChangeDesign
      v-if="updateDesignVisible"
      :visible.sync="updateDesignVisible"
      @confirm="confirm"
    />

    <SkuUploadTech
      :title="$l('dialog_title_upload_tech') || '分配工艺说明'"
      :visible.sync="uploadTechOpts.visible"
      :listAmzSkuCode="uploadTechOpts.listAmzSkuCode"
      @success="resetChecked"
    />
  </div>
</template>
<script>
import EditLoading from '@/mixins/edit-loading'
import lodash from 'lodash'
import tableColumns from './table-columns'
import ChooseAmzSku from '@/views/ecom/components/choose-amz-sku'
import BaseTable from '@/mixins/base-table'
import AddSku from '../add-sku'
import Header from './header.vue'
import ChangeDesign from '@/views/ecom/components/change-design'
import SkuUploadTech from '../sku-upload-tech'
import tableOpts from './table-opts'
export default {
  mixins: [EditLoading, BaseTable],
  components: { ChooseAmzSku, AddSku, ChangeDesign, Header, SkuUploadTech },
  props: {
    amzSpuCode: { type: String, default: '' },
    addSku: { type: Boolean, default: false },
    form: { type: Object, default: () => {} },
  },
  data() {
    return {
      usePage: true,
      loading: false,
      checkedCount: 0,
      updateDesignVisible: false,

      amzSkuOption: {
        amzSkuCode: '',
        addAmzSkuVisible: false,
        isSpuCopy: false,
        title: '',
        amzSkudisabled: false,
      },
      checkedRow: [],
      tableToolbar: {
        slots: { buttons: 'toolbar_form' },
        // refresh: { query: () =>  this.$refs.header.query() },
      },
      tableColumns: tableColumns(this),
      chooseAmzSkuVisible: false,
      uploadTechOpts: { visible: false, listAmzSkuCode: [] },
    }
  },
  computed: {
    tableOpts() {
      const opts = tableOpts(this)
      return opts.filter(({ allow }) => (!allow ? true : this.allow(allow)))
    },
  },
  methods: {
    getData(form) {
      this.loading = true
      this.$api
        .ecomAmzSkuListBySpuCodeApi({
          amzSpuCode: this.amzSpuCode,
          ...this.page,
          ...form,
        })
        .then((res) => {
          this.loading = false
          this.setData(res)
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetChecked() {
      this.$refs.vxeGrid.clearCheckboxRow()
      this.$refs.vxeGrid.clearCheckboxReserve()
      this.checkedCount = 0
      this.checkedRow = []
    },
    handleCommand({ opt, data }) {
      const { pageType } = opt
      if (!pageType) return
      const { amzSkuCode } = data
      const types = {
        edit: this.$l('INDEX.dialog_title_edit_SKU') || '编辑SKU',
        add: this.$l('INDEX.dialog_title_Add_SKU') || '新增SKU',
        detail: this.$l('INDEX.dialog_title_SKU_detail') || 'SKU详情',
        copy: '复制SKU',
      }
      switch (pageType) {
        case 'detail':
        case 'edit':
        case 'add':
        case 'copy':
          this.amzSkuOption = {
            amzSkuCode: amzSkuCode,
            addAmzSkuVisible: true,
            title: types[pageType],
            amzSkudisabled: pageType === 'detail',
            isSpuCopy: pageType === 'copy',
          }
          break
        case 'del':
          this.del({ amzSkuCode })
          break
        case 'submit':
          this.informationReviewApi([amzSkuCode])
          break
        case 'change':
          this.purchasableApplyApi([amzSkuCode])
          break
      }
    },
    del({ amzSkuCode }) {
      this.$confirm(
        this.$l('INDEX.tips_delete') || '是否要删除该记录？',
        this.$l('INDEX.label_completeDesc') || '确认信息',
        {
          confirmButtonText:
            this.$l('INDEX.product_dialog_btn_confirm') || '确定',
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          type: 'warning',
        }
      ).then(async () => {
        const res = await this.$api
          .ecomAmzSkuDelApi({ amzSkuCode })
          .catch(() => null)
        const { status } = res || {}
        if (status !== 200) {
          this.$message.error(this.$l('INDEX.tips_delete_no') || '删除失败！')
          return
        }
        this.$message({
          showClose: true,
          message: this.$l('INDEX.tips_delete_ok') || '删除成功！',
          type: 'success',
        })
        this.$refs.header.query()
      })
    },

    informationReview() {
      if (!this.checkedRow || !this.checkedRow.length) {
        this.$message({
          message: '请选择需要申请信息审核的SKU',
          type: 'warning',
        })
        return
      }
      const listAmzSkuCode = this.checkedRow.map((item) => item.amzSkuCode)
      this.informationReviewApi(listAmzSkuCode)
    },

    async informationReviewApi(listAmzSkuCode) {
      const confirm = await this.$confirm(
        '是否要信息审核？',
        this.$l('INDEX.label_completeDesc') || '确认信息',
        {
          confirmButtonText:
            this.$l('INDEX.product_dialog_btn_confirm') || '确定',
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          type: 'warning',
        }
      )
        .then(() => true)
        .catch(() => false)
      if (!confirm) return
      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomAmzSkuInformationApplyApi({ listAmzSkuCode })
        .catch(() => null)
      this.loadingClose()
      if (!res) return
      const { status } = res
      if (status !== 200) {
        this.$message.error('申请失败！')
        return
      }
      this.$message({
        showClose: true,
        message: '申请成功！',
        type: 'success',
      })
      this.reload()
    },

    purchasableApply() {
      if (!this.checkedRow || !this.checkedRow.length) {
        this.$message({ message: '请选择需要申请可下单的SKU', type: 'warning' })
        return
      }
      const listAmzSkuCode = this.checkedRow.map((item) => item.amzSkuCode)
      this.purchasableApplyApi(listAmzSkuCode)
    },
    async purchasableApplyApi(listAmzSkuCode) {
      const confirm = await this.$confirm(
        '是否要申请可下单？',
        this.$l('INDEX.label_completeDesc') || '确认信息',
        {
          confirmButtonText:
            this.$l('INDEX.product_dialog_btn_confirm') || '确定',
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          type: 'warning',
        }
      )
        .then(() => true)
        .catch(() => false)
      if (!confirm) return
      if (this.loadingOpen()) return
      const res = await this.$api
        .ecomAmzSkuPurchasableApplyApi({ listAmzSkuCode })
        .catch(() => null)
      this.loadingClose()
      if (!res) return
      const { status } = res
      if (status !== 200) {
        this.$message.error('申请失败！')
        return
      }
      this.$message({
        showClose: true,
        message: '申请成功！',
        type: 'success',
      })
      this.$refs.header.query()
    },
    remove() {
      if (!this.checkedRow || !this.checkedRow.length) {
        this.$message({
          message: '请选择需要移除的SKU产品',
          type: 'warning',
        })
        return
      }

      this.$confirm(
        '是否要移除所选产品？',
        this.$l('INDEX.label_completeDesc') || '确认信息',
        {
          confirmButtonText:
            this.$l('INDEX.product_dialog_btn_confirm') || '确定',
          cancelButtonText: this.$l('INDEX.btn_cancel') || '取消',
          type: 'warning',
        }
      ).then(async () => {
        this.loadingOpen()
        const listCode = this.checkedRow.map((item) => item.amzSkuCode)
        const res = await this.$api
          .ecomAmzSpuRemoveSkuApi({ listCode, amzSpuCode: this.amzSpuCode })
          .catch(() => null)
        this.loadingClose()
        const { status } = res || {}
        if (status !== 200) {
          this.$message.error('移除失败！')
          return
        }
        this.$message({
          showClose: true,
          message: '移除成功！',
          type: 'success',
        })
        this.$refs.header.query()
        this.resetChecked()
      })
    },
    async confirm(form) {
      if (!this.checkedRow || !this.checkedRow.length) {
        this.$message({
          message: '请选择需要调整企划人员的产品',
          type: 'warning',
        })
        return
      }
      const listCode = this.checkedRow.map((item) => item.amzSkuCode)
      this.loadingOpen()
      const res = await this.$api
        .ecomAmzSkuChangeDesignApi({ listCode, ...form })
        .catch(() => null)
      this.loadingClose()
      const { status } = res || {}
      if (status !== 200) {
        this.$message.error('调整失败！')
        return
      }
      this.$message({
        showClose: true,
        message: '调整成功！',
        type: 'success',
      })
      this.$refs.header.query()
      this.resetChecked()
      this.updateDesignVisible = false
    },
    chooseAmzSkuConfirm(data) {
      const listCode = data.map((item) => {
        return item.amzSkuCode
      })
      this.loadingOpen()
      this.$api
        .ecomAmzSpuBindSpuBySpuCodeApi({
          amzSpuCode: this.amzSpuCode,
          listCode: listCode,
          masterCategoryName: this.form.masterCategoryName,
          masterCategoryCode: this.form.masterCategoryCode,
          detailCategoryCode: this.form.categoryCode,
          detailCategoryName: this.form.categoryName,
          isVc: this.form.isVc,
        })
        .then((res) => {
          this.loadingClose()
          this.$message({
            showClose: true,
            message: this.$l('INDEX.tips_save_ok') || res.msg,
            type: 'success',
          })

          this.$refs.header.query()
        })
        .catch(() => {
          this.loadingClose()
        })
    },
    onCheckboxChange({ records, reserves }) {
      this.checkedRow = records.concat(reserves)
      this.checkedCount = !this.checkedRow ? 0 : this.checkedRow.length
    },
    onCellClick({ row, column }) {
      const { property } = column
      const filterColumns = ['checkbox', 'col-opt']
      if (filterColumns.includes(property)) return
      this.$refs.vxeGrid.toggleCheckboxRow(row)
      const data = this.$refs.vxeGrid.getCheckboxRecords(true) || []
      const reserves = this.$refs.vxeGrid.getCheckboxReserveRecords(true) || []
      this.checkedRow = data.concat(reserves)
      this.checkedCount = !this.checkedRow ? 0 : this.checkedRow.length
    },
    headerQuery() {
      this.$refs.header.query()
    },
    uploadTech() {
      if (!this.checkedRow?.length) {
        this.$message({
          message:
            this.$l('tips_sku_upload_tech_empty') ||
            '请选择需要分配工艺说明的产品',
          type: 'warning',
        })
        return
      }

      for (const sku of this.checkedRow) {
        const { amzSkuNo, amzSkuStatus, applicationStatus } = sku

        if (lodash.isEqual(amzSkuStatus, 'D')) {
          this.$message({
            message: `SKU[${amzSkuNo}]已删除，无法分配工艺说明`,
            type: 'warning',
          })
          return
        }
        if (lodash.isEqual(applicationStatus, 'O')) {
          this.$message({
            message: `SKU[${amzSkuNo}]已申请可下单待审批，无法分配工艺说明`,
            type: 'warning',
          })
          return
        }
        if (lodash.isEqual(applicationStatus, 'P')) {
          this.$message({
            message: `SKU[${amzSkuNo}]已申请转正待审批，无法分配工艺说明`,
            type: 'warning',
          })
          return
        }
      }

      this.uploadTechOpts = {
        visible: true,
        listAmzSkuCode: this.checkedRow.map((item) => item.amzSkuCode),
      }
    },
  },
}
</script>
<style lang="scss" scoped>
::v-deep .vxe-table--main-wrapper {
  margin-top: 20px;
}
::v-deep {
  .el-tag--confirm {
    background-color: #f9f0ff !important;
    border-color: #d3adf7 !important;
    color: #732ed1 !important;
  }
}
</style>
